// En: src/components/forms/MapLocationPicker.tsx
import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import styles from './ProjectForm.module.css';

// Fix para iconos de marker
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface MapLocationPickerProps {
  initialUbication?: string; // Recibe el string existente "lat,lng"
  onUbicationChange: (ubication: string) => void; // Devuelve string "lat,lng"
  showMap: boolean;
  onShowMapChange: (show: boolean) => void;
}

function LocationMarker({
  onLocationChange,
  currentLocation
}: {
  onLocationChange: (lat: number, lng: number) => void;
  currentLocation: { lat: number; lng: number } | null;
}) {
  const [position, setPosition] = useState<any>(currentLocation);

  const map = useMapEvents({
    click(e) {
      const { lat, lng } = e.latlng;
      setPosition(e.latlng);
      onLocationChange(lat, lng);
    },
  });

  // 🔧 CORREGIDO: Actualizar posición cuando currentLocation cambia
  useEffect(() => {
    if (currentLocation) {
      setPosition(currentLocation);
      map.setView([currentLocation.lat, currentLocation.lng], 15);
    }
  }, [currentLocation, map]);

  return position === null ? null : (
    <Marker position={position} />
  );
}

// Función helper para parsear la ubicación
const parseUbication = (ubication: string): { lat: number; lng: number } | null => {
  if (!ubication) return null;

  const parts = ubication.split(',');
  if (parts.length === 2) {
    const lat = parseFloat(parts[0].trim());
    const lng = parseFloat(parts[1].trim());
    if (!isNaN(lat) && !isNaN(lng)) {
      return { lat, lng };
    }
  }
  return null;
};

export const MapLocationPicker: React.FC<MapLocationPickerProps> = ({
  initialUbication,
  onUbicationChange,
  showMap,
  onShowMapChange,
}) => {
  const [currentLocation, setCurrentLocation] = useState<{ lat: number; lng: number } | null>(null);

  // 🔧 CORREGIDO: Efecto para cargar la ubicación inicial
  useEffect(() => {
    if (initialUbication) {
      const parsedLocation = parseUbication(initialUbication);
      if (parsedLocation) {
        console.log('📍 Ubicación inicial cargada:', parsedLocation);
        setCurrentLocation(parsedLocation);
      }
    }
  }, [initialUbication]);

  const handleMapClick = (lat: number, lng: number) => {
    const newLocation = { lat, lng };
    console.log('📍 Nueva ubicación seleccionada:', newLocation);
    setCurrentLocation(newLocation);
    // Guardamos como string "lat,lng" en el campo ubication
    onUbicationChange(`${lat},${lng}`);
  };

  const handleRemoveLocation = () => {
    console.log('🗑️ Ubicación eliminada');
    setCurrentLocation(null);
    onUbicationChange(''); // Limpiamos el campo
  };

  const handleUseTextInput = () => {
    setCurrentLocation(null);
    onUbicationChange('');
  };

  // Coordenadas por defecto para México
  const defaultCenter: [number, number] = [23.6345, -102.5528];
  const defaultZoom = 5;
  const selectedZoom = 15;

  return (
    <div className={styles.mapSection}>
      <div className={styles.mapToggle}>
        <label className={styles.toggleLabel}>
          <input
            type="checkbox"
            checked={showMap}
            onChange={(e) => onShowMapChange(e.target.checked)}
          />
          Mostrar mapa en la página del proyecto
        </label>
      </div>

      {showMap && (
        <div className={styles.mapContainer}>
          <div className={styles.mapInstructions}>
            <p>
              {currentLocation
                ? 'Ubicación seleccionada - Haz clic para cambiar'
                : 'Haz clic en el mapa para marcar la ubicación'}
            </p>
            <div className={styles.mapActions}>
              {currentLocation && (
                <button
                  type="button"
                  className={styles.removeLocationButton}
                  onClick={handleRemoveLocation}
                >
                  Eliminar ubicación
                </button>
              )}
              <button
                type="button"
                className={styles.textInputButton}
                onClick={handleUseTextInput}
              >
                Usar dirección textual
              </button>
            </div>
          </div>

          <MapContainer
            center={currentLocation || defaultCenter}
            zoom={currentLocation ? selectedZoom : defaultZoom}
            style={{ height: '400px', width: '100%' }}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            />
            <LocationMarker
              onLocationChange={handleMapClick}
              currentLocation={currentLocation}
            />
          </MapContainer>

          {currentLocation && (
            <div className={styles.locationInfo}>
              <p> Ubicación seleccionada:</p>
              <p><strong>Lat:</strong> {currentLocation.lat.toFixed(6)}</p>
              <p><strong>Lng:</strong> {currentLocation.lng.toFixed(6)}</p>
              <p><strong>Zoom:</strong> {selectedZoom}</p>
            </div>
          )}
        </div>
      )}

      {/* Mostrar input de texto si no hay mapa o si el usuario quiere texto */}
      {(!showMap || !currentLocation) && (
        <div className={`${styles.textInputFallback} ${currentLocation ? styles.withMapLocation : ''}`}>
          <label htmlFor="ubication"> Ubicación (texto)</label>
          <input
            type="text"
            id="ubication"
            name="ubication"
            value={!currentLocation ? initialUbication || '' : `${currentLocation.lat.toFixed(4)}, ${currentLocation.lng.toFixed(4)}`}
            onChange={(e) => onUbicationChange(e.target.value)}
            placeholder="Ej: Av. Corrientes 1234, CABA"
            disabled={!!currentLocation}
          />
          {currentLocation && (
            <p className={styles.helperText}>
               Para usar texto, primero elimina la ubicación del mapa
            </p>
          )}
        </div>
      )}
    </div>
  );
};